import axios from 'axios';
import {
  FETCH_EVENTS, FETCH_EVENT, FETCH_ACTIVE_EVENTS, FETCH_REASONS_TO_ATTEND,
  FETCH_VENUE, FETCH_VENUE_CITY_IMAGES, KNOWLEDGE_SHARING_SESSION,
  STRATEGIC_SPONSORS, PARALLEL_EVENTS, ACCOMMODATIONS, SPEAKERS_LIST,
  EXHIBITORS, MEETING_ROOMS, EVENT_PRICES,
  AWARD_PRICES, FETCH_PREVIOUS_EDITION, FETCH_EVENT_ALL_SPONSORS, PARTICIPANT_COMPANIES,
  BOOKED_TABLES,
} from './types';
import api from '../../../utils/api';

// Tracks which eventId each stateful action last completed for, preventing
// redundant network requests when multiple tabs share the same data.
const _fetchedForEvent = {};

// In-flight deduplication: concurrent callers for the same key share one
// Promise instead of each making a separate HTTP request.
// Keys in use:
//   'event:{eventId}'            — fetchEvent(eventId)
//   'eventPrices:{eventId}'      — fetchEventPrices(eventId)
//   'awardPrices:{awardId}'      — fetchAwardPrices(awardId)
//   'eventCategories:{eventId}'  — fetchEventCategories(eventId)
//   'eventSponsors:{categoryId}:show_all=true' — fetchEventSponsors(categoryId)
//   'participantMembers:{eventId}:{searchName}:{page}:display=true:{orderBy}:{myNetwork}' — fetchParticipantMembers(params)
//   'participantCompanies:{eventId}:{searchName}:{offset}:{limit}' — fetchParticipantCompanies(fetchData)
const _pendingActions = {};

// Completed-fetch cache: sequential callers skip re-fetching already-loaded data.
// Keys in use:
//   'eventSponsors:{categoryId}:show_all=true' — fetchEventSponsors
//   'participantMembers:{...}'                 — fetchParticipantMembers
//   'agenda:{eventId}'                         — fetchAgenda
//   'agendaMaps:{eventId}'                     — fetchAgendaMaps
//   'meetingPlannerDays:{eventId}'             — fetchMeetingPlannerDays
const _completedActions = {};

export default {
  // @Fetch all events
  async fetchEvents({ commit }, tag = '') {
    const { data } = await axios(api.events.fetch(tag));
    commit(FETCH_EVENTS, { events: data.results });
  },
  async fetchPastEvents(_, page = 1) {
    const { data } = await axios(api.events.fetchPastEvents(page));
    return data;
  },
  // @Fetch single event
  async fetchEvent({ commit, state }, { eventId, router }) {
    if (!eventId) return;
    if (state.event && String(state.event.id) === String(eventId)) return state.event;
    const key = `event:${eventId}`;
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.events.fetchSingleEvent(eventId));
        commit(FETCH_EVENT, { event: data });
        return data;
      } catch (error) {
        if (error.response && error.response.status === 404 && router) {
          router.push({ name: 'ccAllEvents', query: { 'inactive-event-redirect': 'true' } });
        }
        throw error;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async fetchPreviousEdition({ commit }, info) {
    const { data } = await axios(api.events.fetchPreviousEdition(info.id, info.type));
    commit(FETCH_PREVIOUS_EDITION, { prevEvent: data });
    return data;
  },
  // @Fetch single event sections
  async fetchReasonsToAttend({ commit }, eventId) {
    if (_fetchedForEvent.reasonsToAttend === eventId) return;
    const { data } = await axios(api.events.fetchReasonsToAttend(eventId));
    commit(FETCH_REASONS_TO_ATTEND, { reasons: data.results });
    _fetchedForEvent.reasonsToAttend = eventId;
  },
  async fetchVenue({ commit }, eventId) {
    if (_fetchedForEvent.venue === eventId) return;
    const { data } = await axios(api.events.fetchVenue(eventId));
    commit(FETCH_VENUE, { venue: data.results[0] || {} });
    _fetchedForEvent.venue = eventId;
  },
  async fetchAllVenues() {
    const { data } = await axios(api.events.fetchAllVenues());
    return data;
  },
  async fetchVenueCityImages({ commit }, eventId) {
    if (_fetchedForEvent.venueCityImages === eventId) return;
    const { data } = await axios(api.events.fetchVenueCityImages(eventId));
    commit(FETCH_VENUE_CITY_IMAGES, { venueCityImages: data.results });
    _fetchedForEvent.venueCityImages = eventId;
  },
  async fetchKnowledgeSharingSession({ commit }, eventId) {
    if (_fetchedForEvent.knowledgeSharingSession === eventId) return;
    const { data } = await axios(api.events.knowledgeSharingSession(eventId));
    commit(KNOWLEDGE_SHARING_SESSION, { session: data.results });
    _fetchedForEvent.knowledgeSharingSession = eventId;
  },
  async strategicSponsors({ commit }, eventId) {
    if (_fetchedForEvent.strategicSponsors === eventId) return;
    const { data } = await axios(api.events.strategicSponsors(eventId));
    commit(STRATEGIC_SPONSORS, { sponsors: data.results });
    _fetchedForEvent.strategicSponsors = eventId;
  },
  async fetchParallelEvents({ commit }, eventId) {
    if (_fetchedForEvent.parallelEvents === eventId) return;
    const { data } = await axios(api.events.parallelEvents(eventId));
    commit(PARALLEL_EVENTS, { parallelEvents: data.results });
    _fetchedForEvent.parallelEvents = eventId;
  },
  async fetchAccommodations({ commit }, eventId) {
    if (_fetchedForEvent.accommodations === eventId) return;
    const { data } = await axios(api.events.accommodations(eventId));
    commit(ACCOMMODATIONS, { accommodations: data.results[0] });
    _fetchedForEvent.accommodations = eventId;
  },
  async fetchSpeakersList({ commit }, eventId) {
    if (_fetchedForEvent.speakersList === eventId) return;
    const { data } = await axios(api.events.speakersList(eventId));
    commit(SPEAKERS_LIST, { speakersList: data.results });
    _fetchedForEvent.speakersList = eventId;
  },
  async fetchExhibitors({ commit }, eventId) {
    if (_fetchedForEvent.exhibitors === eventId) return;
    const { data } = await axios(api.events.exhibitors(eventId));
    commit(EXHIBITORS, { exhibitors: data.results });
    _fetchedForEvent.exhibitors = eventId;
  },
  async fetchBookedTables({ commit }, eventId) {
    if (_fetchedForEvent.bookedTables === eventId) return;
    const { data } = await axios(api.events.bookedTables(eventId));
    commit(BOOKED_TABLES, { bookedTables: data.results });
    _fetchedForEvent.bookedTables = eventId;
  },
  async fetchMeetingRooms({ commit }, eventId) {
    if (_fetchedForEvent.meetingRooms === eventId) return;
    const { data } = await axios(api.events.meetingRooms(eventId));
    commit(MEETING_ROOMS, { meetingRooms: data.results });
    _fetchedForEvent.meetingRooms = eventId;
  },
  async fetchMediaDocs({}, reqData) {
    const data = await axios(api.events.mediaDocs(reqData));
    return data;
  },
  async fetchParticipantMembers(_, params) {
    const key = `participantMembers:${params.eventId}:${params.searchName || ''}:${params.page || 1}:display=true:${params.orderBy || ''}:${params.myNetwork ? 'my_network=true' : ''}`;
    if (_completedActions[key]) return _completedActions[key];
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.events.participantMembers(params));
        _completedActions[key] = data;
        return data;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async fetchParticipantMembersforCheck({}, eventId) {
    const { data } = await axios(api.events.participantMembers({ eventId }));
    return data.results;
  },
  async fetchParticipantCompanies({ commit }, fetchData) {
    const key = `participantCompanies:${fetchData.eventId}:${fetchData.searchName || ''}:${fetchData.offset || 0}:${fetchData.limit || 10}`;
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.events.participantCompanies(fetchData));
        commit(PARTICIPANT_COMPANIES, { data });
        return data;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async fetchEventPrices({ commit }, eventId) {
    if (_fetchedForEvent.eventPrices === eventId) return;
    const key = `eventPrices:${eventId}`;
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.events.eventPrices(eventId));
        commit(EVENT_PRICES, { eventPrices: data.results });
        _fetchedForEvent.eventPrices = eventId;
        return data;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async fetchAwardPrices({ commit }, awardId) {
    if (_fetchedForEvent.awardPrices === awardId) return;
    const key = `awardPrices:${awardId}`;
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.events.awardPrices(awardId));
        commit(AWARD_PRICES, { awardPrices: data.results });
        _fetchedForEvent.awardPrices = awardId;
        return data;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async fetchAgenda(_, eventId) {
    const key = `agenda:${eventId}`;
    if (_completedActions[key]) return _completedActions[key];
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.events.fetchAgenda(eventId));
        _completedActions[key] = data.results;
        return data.results;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async fetchMeetingPlannerDays(_, eventId) {
    const key = `meetingPlannerDays:${eventId}`;
    if (_completedActions[key]) return _completedActions[key];
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.events.fetchMeetingPlannerDays(eventId));
        _completedActions[key] = data.results;
        return data.results;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async fetchAgendaMaps(_, eventId) {
    const key = `agendaMaps:${eventId}`;
    if (_completedActions[key]) return _completedActions[key];
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.events.fetchAgendaMaps(eventId));
        _completedActions[key] = data.results;
        return data.results;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async fetchNearbyHotels({}, eventId) {
    const { data } = await axios(api.events.fetchNearbyHotels(eventId));
    return data.results;
  },
  async fetchGallery({}, eventId) {
    const { data } = await axios(api.events.fetchGallery(eventId));
    return data.results;
  },
  async eventBooking({}, bookingDetails) {
    const { data } = await axios(api.events.eventBooking(bookingDetails));
    return data;
  },
  async fetchEventsTags() {
    const { data } = await axios(api.events.fetchEventsTags());
    return data.results;
  },
  async fetchAllEvents({ commit }, tag) {
    const { data } = await axios(api.events.fetchAllEvents(tag));
    commit(FETCH_EVENTS, { events: data.results });
  },
  async getAllEvents(_, { page }) {
    const { data } = await axios(api.events.getAllEvents(page));
    return data;
  },
  async fetchActiveEvents({ commit }, tag) {
    const { data } = await axios(api.events.fetchActiveEvents(tag));
    commit(FETCH_ACTIVE_EVENTS, { activeEvents: data.results });
  },
  async fetchUpcomingEvents() {
    const { data } = await axios(api.events.fetchUpcomingEvents());
    return data.results;
  },
  async fetchEventCategories(_, eventId) {
    const key = `eventCategories:${eventId}`;
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.events.fetchEventCategories(eventId));
        return data.results;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async becomePanelistOrSponsor({}, panelistSponsorData) {
    const data = await axios(api.events.becomePanelistOrSponsor(panelistSponsorData));
    return data;
  },
  async fetchEventSponsors(_, categoryId) {
    const key = `eventSponsors:${categoryId}:show_all=true`;
    if (_completedActions[key]) return _completedActions[key];
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.events.fetchEventSponsors(categoryId));
        _completedActions[key] = data.results;
        return data.results;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async fetchEventAllSponsors({ commit }, id) {
    if (_fetchedForEvent.eventAllSponsors === id) return;
    const { data } = await axios(api.events.fetchEventAllSponsors(id));
    commit(FETCH_EVENT_ALL_SPONSORS, { sponsors: data.results });
    _fetchedForEvent.eventAllSponsors = id;
    return data;
  },
  async fetchBookedEvents(_, customerId) {
    const { data } = await axios(api.events.fetchBookedEvents(customerId));
    return data.results;
  },

  async getEventParticipantMember(_, details) {
    const { data } = await axios(api.events.getEventParticipantMember(details));
    return data.results;
  },
  async fetchEventHashtags(_, eventId) {
    const { data } = await axios(api.events.fetchEventHashtags(eventId));
    const record = data.results && data.results[0];
    return record ? (record.hashtags || []) : [];
  },
};
